import ticketAccountController from '../../controllers/tickets/ticketaccount.controller.js'
import ticketGroupController from '../../controllers/tickets/ticketgroup.controller.js'

import { useAuthStore } from '../../stores/authStore.js'
import { useTicketStore } from '../../stores/tickets/ticketStore.js'

import ticketSettingsHostUtility from '../../utils/ticketSettingsHost.utility.js'

const GROUP_PREFIX = 'TicketGroup:'
const ACCOUNT_PREFIX = 'TicketAccount:'
const inflight = new Map()

function allowedAccountIds(user) {
  return new Set((user?.allowedTicketAccountIds ?? []).map(String))
}

async function fetchTicketGroupHost(groupId, user) {
  const key = String(groupId)
  if (!ticketSettingsHostUtility.groupExists(user, key)) return null
  if (inflight.has(`g:${key}`)) return inflight.get(`g:${key}`)

  const promise = ticketGroupController.get
    .settings(key, { silent: true })
    .then((settings) => ticketSettingsHostUtility.slimFromSource(settings))
    .finally(() => inflight.delete(`g:${key}`))

  inflight.set(`g:${key}`, promise)
  return promise
}

async function fetchTicketAccountHost(accountId, user, { skipGroup = false } = {}) {
  const key = String(accountId)

  if (!skipGroup) {
    const group = ticketSettingsHostUtility.findGroupForAccount(user, key)
    if (group?._id) return fetchTicketGroupHost(String(group._id), user)
  }

  if (inflight.has(`a:${key}`)) return inflight.get(`a:${key}`)

  const promise = ticketAccountController.get
    .settings(key, { silent: true })
    .then((settings) => ticketSettingsHostUtility.slimFromSource(settings))
    .finally(() => inflight.delete(`a:${key}`))

  inflight.set(`a:${key}`, promise)
  return promise
}

/**
 * Fill `ticketStore.settingsHosts` with workflow trees for list rows.
 * Uses the list map when present, then fetches any missing TicketGroup
 * and allowed TicketAccount hosts.
 */
export function useTicketSettingsHosts() {
  const authStore = useAuthStore()
  const ticketStore = useTicketStore()

  async function hydrateForTickets(tickets = []) {
    if (!tickets.length) return

    const map = { ...ticketStore.settingsHosts }
    const missing = ticketSettingsHostUtility.missingKeys(tickets, map, authStore.user)

    if (!missing.length) return

    const allowed = allowedAccountIds(authStore.user)

    const fetched = await Promise.all(
      missing.map(async (hostKey) => {
        if (hostKey.startsWith(GROUP_PREFIX)) {
          const groupId = hostKey.slice(GROUP_PREFIX.length)
          let host = await fetchTicketGroupHost(groupId, authStore.user)
          if (!host) {
            const ticket = tickets.find((row) => {
              if (String(row.ticketGroupId) === groupId) return true
              if (row.settingsHostKey === hostKey) return true
              return ticketSettingsHostUtility.resolveKey(row, authStore.user) === hostKey
            })
            const accountId = ticket?.createdBy != null ? String(ticket.createdBy) : ''
            if (accountId && allowed.has(accountId)) {
              host = await fetchTicketAccountHost(accountId, authStore.user, { skipGroup: true })
            }
          }
          if (!host) return null
          return [hostKey, host]
        }

        if (hostKey.startsWith(ACCOUNT_PREFIX)) {
          const accountId = hostKey.slice(ACCOUNT_PREFIX.length)
          if (!allowed.has(accountId)) return null
          const host = await fetchTicketAccountHost(accountId, authStore.user)
          if (!host) return null
          return [hostKey, host]
        }

        return null
      }),
    )

    const patch = Object.fromEntries(fetched.filter(Boolean))
    if (Object.keys(patch).length) ticketStore.mergeSettingsHosts(patch)
  }

  return { hydrateForTickets }
}
